import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";
import LoginPage from "./routes/auth/login";
import SignupPage from "./routes/auth/signup";
import ForgotPasswordPage from "./routes/auth/forgot-password";
import ProtectedLayout from "@/components/layouts/ProtectedLayout";
import GuestLayout from "@/components/layouts/GuestLayout";
import RootLayout from "@/components/layouts/RootLayout";
import VerificationPage from "./routes/auth/verify";
import TrendsPage from "./routes/dashboard/trends";
import ReferencesPage from "./routes/dashboard/references";
import JoinPage from "./routes/auth/join";
import NotFoundPage from "./routes/not-found";
import LandingPage from "./routes/landing";
import TermsOfUsePage from "./routes/legal/terms-of-use";
import PrivacyPolicyPage from "./routes/legal/privacy-policy";
import ClientLinkMovedView from "./routes/client-portal/ClientLinkMovedView";
import CockpitLayout, {
  HomeRoute,
  UniverseRoute,
  SignalsRoute,
} from "./routes/dashboard/CockpitLayout";
import StrategyRoute from "./routes/dashboard/strategy/guest/StrategyRoute";
import ContentCalendarRoute from "./routes/dashboard/content-calendar/guest/ContentCalendarRoute";
import GuestCalendarView from "./routes/dashboard/content-calendar/guest/GuestCalendarView";
import WorkshopView from "./routes/dashboard/views/WorkshopView";
import SettingsView from "./routes/dashboard/views/SettingsView";
import AuthorizeConnectorPage from "./routes/connect/AuthorizeConnectorPage";
import { isDevMode } from "@/config/envvars";

const AgentRunsView = lazy(() => import("./routes/dashboard/views/AgentRunsView"));
const BrandMediaView = lazy(() => import("./routes/dashboard/views/BrandMediaView"));
function SiteRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        {/* Public landing page — fully developed marketing homepage so the
            site is never login-only. Legal links are visible in its header
            and footer without auth. */}
        <Route path="/" element={<LandingPage />} />

        {/* Public legal pages — available to guests and signed-in users.
            /terms-of-service is an alias matching the URL submitted to
            TikTok's developer review. */}
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/terms-of-service" element={<TermsOfUsePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />

        {/* Review links emailed before clients became Guests. They no longer
            open anything: the page says reviews moved to the client's account
            and links to sign-in. Outside ProtectedLayout on purpose. */}
        <Route path="/client/calendar/:token" element={<ClientLinkMovedView />} />

        <Route path="/client/strategy/:token" element={<ClientLinkMovedView />} />

        <Route element={<ProtectedLayout />}>
          {/* Creative OS Home cockpit — one shell forever (G6). Full-bleed
              (its own NavRail/TopBar/Chat). Path segments match the routes'
              original pre-cockpit slugs (brand-universe/market-signals/
              content-calendar, not the shorter dev-preview names) since
              reused deep components (e.g. content-calendar/components/
              PitchBoard.tsx) hardcode links to them. */}
          <Route path="/dashboard" element={<CockpitLayout />}>
            <Route index element={<HomeRoute />} />
            <Route path="brand-universe" element={<UniverseRoute />} />
            <Route path="market-signals" element={<SignalsRoute />} />
            <Route path="strategy" element={<StrategyRoute />} />
            <Route path="content-calendar" element={<ContentCalendarRoute />} />
            {/* The client review of a month (the share-link portal's view), in-app. */}
            <Route path="content-review" element={<GuestCalendarView />} />
            <Route path="workshop" element={<WorkshopView />} />
            <Route path="settings" element={<SettingsView />} />
            <Route path="trends" element={<TrendsPage />} />
            <Route path="references" element={<ReferencesPage />} />
            {/* Ops/debug pages (G4) — dev-only. Brand Media is a product
                surface gated here until we open it to everyone; the view
                itself does not check isDevMode. */}
            {isDevMode && (
              <>
                <Route path="agent-runs" element={<Suspense fallback={null}><AgentRunsView /></Suspense>} />
                <Route path="brand-media" element={<Suspense fallback={null}><BrandMediaView /></Suspense>} />
              </>
            )}
          </Route>
          <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
          {/* Where Claude, ChatGPT and other MCP connectors send a person to allow them. */}
          <Route path="/connect/authorize" element={<AuthorizeConnectorPage />} />
        </Route>

        {/* Getting in (Figma 10). Each page draws its own PublicShell. */}
        <Route element={<GuestLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify" element={<VerificationPage />} />
        </Route>
        {/* Outside GuestLayout: a reset ends signed in on its own "Password
            changed" page, and an invitation link must open for someone signed
            in as another address (Join 05). */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/join/:inviteId" element={<JoinPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default SiteRoutes;
